import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toastActions } from '@/store/slices/toastSlice';
import { audioLevelStore } from '@/stores/audioLevelStore';

type QualityLevel = 'good' | 'normal' | 'bad';

const NETWORK_GOOD_THRESHOLD = 40;
const NETWORK_BAD_THRESHOLD = 20;
const MIC_WARNING_COOLDOWN_MS = 3 * 60 * 1000;
const MIC_SEVERE_LEVEL_THRESHOLD = 0.82;
const MIC_CLIPPING_LEVEL_THRESHOLD = 0.97;
const MIC_SEVERE_DURATION_MS = 2500;
const MIC_CLIPPING_DURATION_MS = 800;

const getQualityLevel = (quality: number): QualityLevel => {
  if (quality >= NETWORK_GOOD_THRESHOLD) return 'good';
  if (quality >= NETWORK_BAD_THRESHOLD) return 'normal';
  return 'bad';
};

const qualityRank: Record<QualityLevel, number> = {
  good: 0,
  normal: 1,
  bad: 2,
};

let isExitInProgress = false;

export function suppressLocalEnvironmentWarningsForExit(): void {
  isExitInProgress = true;
}

/**
 * 내 회의 환경이 나빠질 때만 전역 스낵바로 알려준다.
 * - 네트워크: good -> normal/bad 또는 normal -> bad 전이 시 즉시 알림
 * - 마이크: 심각한 입력 피크가 지속될 때 알림, 3분 쿨타임 적용
 */
export function useLocalEnvironmentWarnings(): void {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const audioLevels = useSyncExternalStore(
    audioLevelStore.subscribe,
    () => audioLevelStore.getSnapshot()
  );
  const localAudioLevel = [local.jitsiId, local.userId, local.wehagoId]
    .filter(Boolean)
    .reduce((maxLevel, id) => Math.max(maxLevel, audioLevels[id!] ?? 0), 0);

  const previousQualityLevelRef = useRef<QualityLevel>('good');
  const wasReconnectingRef = useRef(false);
  const severeMicStartedAtRef = useRef<number | null>(null);
  const clippingMicStartedAtRef = useRef<number | null>(null);
  const lastMicWarningAtRef = useRef(0);

  useEffect(() => {
    isExitInProgress = false;

    const suppressWarnings = () => {
      suppressLocalEnvironmentWarningsForExit();
    };

    const resetSuppression = () => {
      isExitInProgress = false;
    };

    window.addEventListener('beforeunload', suppressWarnings);
    window.addEventListener('pagehide', suppressWarnings);
    window.addEventListener('pageshow', resetSuppression);

    return () => {
      window.removeEventListener('beforeunload', suppressWarnings);
      window.removeEventListener('pagehide', suppressWarnings);
      window.removeEventListener('pageshow', resetSuppression);
    };
  }, []);

  useEffect(() => {
    if (isExitInProgress) {
      previousQualityLevelRef.current = getQualityLevel(local.connectionQuality);
      wasReconnectingRef.current = local.isReconnecting;
      return;
    }

    if (!local.isConnected) {
      previousQualityLevelRef.current = 'good';
      wasReconnectingRef.current = false;
      return;
    }

    const currentLevel = getQualityLevel(local.connectionQuality);
    const previousLevel = previousQualityLevelRef.current;
    const isWorse = qualityRank[currentLevel] > qualityRank[previousLevel];

    if (isWorse && currentLevel !== 'good') {
      dispatch(toastActions.showSnackbar({
        type: currentLevel === 'bad' ? 'error' : 'info',
        message: currentLevel === 'bad'
          ? '네트워크 상태가 좋지 않습니다. Wi-Fi 또는 유선 연결 상태를 확인해 주세요.'
          : '네트워크 상태가 불안정합니다. 화질이나 음성이 일시적으로 끊길 수 있습니다.',
      }));
    }

    if (!wasReconnectingRef.current && local.isReconnecting) {
      dispatch(toastActions.showSnackbar({
        type: 'error',
        message: '회의 연결이 불안정합니다. 네트워크 상태를 확인해 주세요.',
      }));
    }

    previousQualityLevelRef.current = currentLevel;
    wasReconnectingRef.current = local.isReconnecting;
  }, [dispatch, local.connectionQuality, local.isConnected, local.isReconnecting]);

  useEffect(() => {
    if (isExitInProgress) {
      severeMicStartedAtRef.current = null;
      clippingMicStartedAtRef.current = null;
      return;
    }

    if (!local.isConnected || local.audioMuted || !local.hasAudioTrack) {
      severeMicStartedAtRef.current = null;
      clippingMicStartedAtRef.current = null;
      return;
    }

    const now = Date.now();
    const level = Number.isFinite(localAudioLevel) ? localAudioLevel : 0;

    if (level >= MIC_SEVERE_LEVEL_THRESHOLD) {
      severeMicStartedAtRef.current ??= now;
    } else {
      severeMicStartedAtRef.current = null;
    }

    if (level >= MIC_CLIPPING_LEVEL_THRESHOLD) {
      clippingMicStartedAtRef.current ??= now;
    } else {
      clippingMicStartedAtRef.current = null;
    }

    const severeDuration = severeMicStartedAtRef.current == null ? 0 : now - severeMicStartedAtRef.current;
    const clippingDuration = clippingMicStartedAtRef.current == null ? 0 : now - clippingMicStartedAtRef.current;
    const shouldWarnMic = severeDuration >= MIC_SEVERE_DURATION_MS || clippingDuration >= MIC_CLIPPING_DURATION_MS;

    if (shouldWarnMic && now - lastMicWarningAtRef.current >= MIC_WARNING_COOLDOWN_MS) {
      lastMicWarningAtRef.current = now;
      severeMicStartedAtRef.current = now;
      clippingMicStartedAtRef.current = now;
      dispatch(toastActions.showSnackbar({
        type: 'error',
        message: '마이크에서 큰 소음이나 하울링이 감지됩니다. 스피커 볼륨을 낮추거나 이어폰을 사용해 주세요.',
      }));
    }
  }, [dispatch, local.audioMuted, local.hasAudioTrack, local.isConnected, localAudioLevel]);
}